'use client';

import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { useCookie } from 'react-use';

import { parseJsonSafe } from '@/lib/parseJsonSafe';

const POSTHOG_HOST = 'https://eu.i.posthog.com';
const NEXT_PUBLIC_ANALYTICS_ENABLED = parseJsonSafe(
    process.env.NEXT_PUBLIC_ANALYTICS_ENABLED,
    false,
);

export const initialisePosthogAnalytics = () => {
    posthog.set_config({ persistence: 'localStorage+cookie' });
};

export function PostHogContextProvider({ children }: { children: React.ReactNode }) {
    const [isCustomerCookie] = useCookie('prezly_customer');
    const isCustomer = isCustomerCookie === '1';

    useEffect(() => {
        if (!NEXT_PUBLIC_ANALYTICS_ENABLED) {
            return;
        }

        // https://posthog.com/docs/libraries/js
        // https://posthog.com/tutorials/nextjs-cookie-banner
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
            api_host: '/ph',
            person_profiles: 'always',
            persistence: 'memory',
            autocapture: false,
            // Disable automatic pageview capture, as we capture manually in PostHogPageView
            // @see https://posthog.com/docs/libraries/next-js#router-specific-instructions
            capture_pageview: false,
            capture_pageleave: true,
            session_recording: {
                maskAllInputs: false,
                maskInputOptions: {
                    password: true,
                },
            },
            ui_host: POSTHOG_HOST,
            before_send(data) {
                if (!data) {
                    return data;
                }

                return {
                    ...data,
                    properties: {
                        ...data.properties,
                        is_customer: isCustomer,
                    },
                };
            },
        });
    }, []);

    return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
